import { createTheme, type CustomFlowbiteTheme } from "flowbite-react";
import cn from "./utils/classNameMerge";

const logrockTheme: CustomFlowbiteTheme = createTheme({
  button: {
    base: "rounded-lg",
    inner: {
      base: "flex items-center justify-center transition-all duration-200",
    },
    color: {
      // info is the default button color
      gray: "text-neutral-700 bg-white border border-gray-200 enabled:hover:bg-neutral-50 enabled:hover:text-neutral-700 :ring-cyan-700 focus:text-cyan-700 focus:ring-2 hover:bg-neutral-50 hover:text-neutral-700",
      link: "text-primary-500 underline text-neutral-900 focus:ring-0",
      info: "logrock-button text-neutral-400 bg-neutral-200 border border-neutral-200 enabled:text-neutral-50 enabled:bg-neutral-900 enabled:hover:bg-neutral-700 focus:ring-4",
      dark: "border border-transparent bg-gray-800 text-white focus:ring-4 focus:ring-gray-300 enabled:hover:bg-gray-900 disabled:bg-neutral-100 disabled:text-neutral-400 shadow-small",
      warning:
        "border border-transparent bg-destructive-50 text-destructive-600 focus:ring-4 focus:ring-red-300 disabled:bg-neutral-100 disabled:text-neutral-400 shadow-small",
      primary:
        "bg-primary-500 text-neutral-900 hover:bg-primary-600 focus:ring-4 focus:ring-primary-300 disabled:bg-neutral-100 disabled:text-neutral-400 shadow-small",
    },
    disabled: "cursor-not-allowed",
    outline: {
      color: {
        default: "border-0",
        gray: "border border-gray-900",
      },
      off: "",
      on: "flex justify-center bg-white text-gray-900 transition-all duration-75 ease-in group-enabled:group-hover:bg-white/0 group-hover:group-enabled:text-neutral-100 w-full",
      pill: {
        off: "rounded-lg",
        on: "rounded-full",
      },
    },
  },
  checkbox: {
    root: {
      base: "h-xbig w-xbig rounded focus:ring-2 border bg-white border-gray-300 my-2 checkbox cursor-pointer",
      color: {
        default:
          "focus:ring-primary-500 text-primary-500 checked:!bg-[#f1b72c] checked:!border-[#f1b72c] checked:before:!bg-white",
        disabled: "text-neutral-300 focus:ring-0 focus:ring-transparent",
      },
    },
  },
  radio: {
    root: {
      base: cn(
        "h-xbig w-xbig border-gray-300 border border-solid relative",
        "focus:outline-transparent focus:ring-transparent",
        "checked:border-primary-500 checked:border-2",
        "checked:before:content-[''] checked:before:absolute checked:before:size-3 ",
        "checked:before:top-1/2 checked:before:left-1/2 checked:before:-translate-x-1/2 checked:before:-translate-y-1/2",
        "checked:before:bg-primary-500 checked:before:rounded-full",
        "checked:before:ring-white checked:before:ring-4",
      ),
    },
  },
  toggleSwitch: {
    root: {
      label: "ml-3 text-sm font-medium text-neutral-900",
    },
    toggle: {
      base: "relative toggle-bg rounded-full border group-focus:ring-4 group-focus:ring-primary-300",
      checked: {
        on: "after:translate-x-full after:border-white",
        off: "border-gray-200 bg-gray-200",
        color: {
          // the default color is blue (?) so we are adding a new one
          primary:
            "bg-primary-500 border-primary-500 after:bg-white after:size-4",
          disabled: "bg-neutral-500 border-neutral-500 group-focus:ring-0",
        },
      },
    },
  },
  floatingLabel: {
    input: {
      default: {
        outlined: {
          sm: "peer block w-full appearance-none rounded-lg bg-transparent px-2.5 pb-2.5 pt-4 text-xs text-gray-900 focus:ring-0 focus:border-1 focus:border-black focus-visible:border-1 focus-visible:border-black focus-visible:ring-0 focus-visible:outline-hidden border-1 border-neutral-300 disabled:text-neutral-400 disabled:bg-neutral-50",
          md: "peer block w-full appearance-none rounded-lg bg-transparent px-2.5 pb-4 pt-4 text-sm text-gray-900 focus:ring-0 focus:border-1 focus:border-black focus-visible:border-1 focus-visible:border-black focus-visible:ring-0 focus-visible:outline-hidden border-1 border-neutral-300 disabled:text-neutral-400 disabled:bg-neutral-50",
        },
      },
      error: {
        outlined: {
          sm: "peer block w-full appearance-none rounded-lg bg-transparent px-2.5 pb-2.5 pt-4 text-xs text-gray-900 focus:ring-0 focus:border-1 focus:border-red-700 focus-visible:border-1 focus-visible:border-red-700 focus-visible:ring-0 focus-visible:outline-hidden border-1 border-red-700 disabled:text-neutral-400",
          md: "peer block w-full appearance-none rounded-lg bg-transparent px-2.5 pb-4 pt-4 text-sm text-gray-900 focus:ring-0 focus:border-1 focus:border-red-700 focus-visible:border-1 focus-visible:border-red-700 focus-visible:ring-0 focus-visible:outline-hidden border-1 border-red-700 disabled:text-neutral-400",
        },
      },
    },
    label: {
      default: {
        outlined: {
          sm: "absolute left-1 top-2 z-10 origin-[0] -translate-y-4 scale-75 transition-transform bg-white peer-disabled:bg-neutral-50 px-2 text-xs text-gray-500 peer-disabled:text-neutral-400 duration-300 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus:top-2 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:px-2 peer-focus:text-neutral-900 dark:bg-gray-900 dark:text-gray-400 dark:peer-focus:text-neutral-500",
          md: "absolute left-1 top-2 z-10 origin-[0] -translate-y-4 scale-75 transition-transform bg-white peer-disabled:bg-neutral-50 px-2 text-sm text-gray-500 peer-disabled:text-neutral-400 duration-300 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus:top-2 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:px-2 peer-focus:text-neutral-900 dark:bg-gray-900 dark:text-gray-400 dark:peer-focus:text-neutral-500 whitespace-nowrap max-w-[calc(100%-8px)] text-ellipsis overflow-hidden peer-focus:max-w-full",
        },
      },
    },
    helperText: {
      // have to set this to an arbitrary value because flowbite is wack
      default: "text-[12px] text-gray-500",
      success: "text-[12px] text-green-500",
      error: "text-[12px] font-bold text-destructive-500",
    },
  },

  select: {
    field: {
      select: {
        sizes: {
          md: "text-sm py-4 px-2",
          sm: "text-xs py-2.5 px-2",
        },
        colors: {
          gray: "text-gray-900 bg-white border border-gray-300 enabled:hover:text-neutral-700 focus:ring-primary-200 focus:text-neutral-900 focus:ring-0 focus:border-neutral-900 disabled:bg-neutral-50",
        },
      },
    },
  },
  badge: {
    root: {
      base: "flex h-fit items-center gap-1 font-semibold rounded-md",
      color: {
        gray: "bg-neutral-100 text-neutral-800 group-hover:bg-neutral-200",
      },
      size: {
        xs: "p-1 text-paragraph-xsmall",
        sm: "p-1.5 text-paragraph-small",
      },
    },
  },
  alert: {
    color: {
      info: "bg-primary-50 text-neutral-600",
    },
  },
  textarea: {
    colors: {
      gray: "bg-neutral-50 border-neutral-300 text-neutral-900 focus:border-neutral-900 focus:ring-neutral-900 p-3",
    },
  },
  progress: {
    color: {
      cyan: "bg-primary-500",
    },
  },
  textInput: {
    field: {
      input: {
        colors: {
          gray: "border-gray-300 bg-gray-50 text-gray-900 focus:border-black focus:ring-0",
        },
        sizes: {
          sm: "text-paragraph-small py-2",
        },
      },
    },
  },
  tooltip: {
    target: "w-fit",
    animation: "transition-opacity",
    arrow: {
      base: "absolute z-10 h-2 w-2 rotate-45 drop-shadow-lg",
      style: {
        light: "bg-white",
      },
      placement: "-4px",
    },
    base: "absolute z-10 inline-block rounded-lg shadow-lg ml-3 rounded-lg p-4 w-[230px] drop-shadow-xl",
    hidden: "invisible opacity-0",
    style: {
      light: "bg-white text-neutral-500 font-normal text-paragraph-small",
    },
    content: "relative z-20",
  },
  modal: {
    root: {
      show: {
        on: "flex bg-neutral-900/20",
      },
    },
  },
});

export default logrockTheme;
